import { Check } from "lucide-react";
import { Button } from "../../../components/Button/Button";
import styles from "./StickySaveBar.module.css";

interface Props {
  /** Whether the bar is visible. Caller controls dirty detection. */
  visible: boolean;
  /** Number of leaf-level changes vs server snapshot. */
  changes: number;
  /** Number of sections with at least one change. */
  sections: number;
  /** True when validation or JSON parse is preventing a save. */
  disabled?: boolean;
  /**
   * What is blocking the save, when the problem has no field of its own to sit
   * under. The row editors flag their own cells, but a list-level rule — a
   * duplicate request type, a quota the server would not read — has nowhere
   * else to be said, and a greyed-out button on its own does not say it.
   *
   * Mockup: t-81/config-help-max-amount.html L455, where the bar carries the
   * message in place of the change count.
   */
  message?: string;
  saving?: boolean;
  onDiscard: () => void;
  onSave: () => void;
}

/**
 * Fixed-bottom save bar shown when there are unsaved changes.
 *
 * Mockup: project-config.html L751-L762. The bar is position: fixed at the
 * bottom of the main column (offset by the sidebar) so it stays pinned above
 * the content while scrolling — same idiom as the Leaderboards config form.
 * Its inner row is capped at --content-max / 32px gutter so the status text
 * and buttons line up with the page body.
 */
export function StickySaveBar({
  visible,
  changes,
  sections,
  disabled,
  message,
  saving,
  onDiscard,
  onSave,
}: Props) {
  if (!visible) return null;
  const sectionWord = sections === 1 ? "section" : "sections";
  const changeWord = changes === 1 ? "change" : "changes";
  return (
    <div className={styles.bar} role="region" aria-label="Unsaved changes">
      <div className={styles.inner}>
        <div className={styles.status}>
          <span className={styles.dot} aria-hidden />
          {message ? (
            <span>{message}</span>
          ) : (
            <>
              <strong>Unsaved changes</strong>
              <span className={styles.count}>
                · {changes} {changeWord} in {sections} {sectionWord}
              </span>
            </>
          )}
        </div>
        <div className={styles.actions}>
          <Button
            variant="secondary"
            size="sm"
            onClick={onDiscard}
            disabled={saving}
          >
            Discard
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onSave}
            disabled={disabled || saving}
            loading={saving}
            icon={!saving ? <Check size={13} aria-hidden /> : undefined}
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
